import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Building2, Phone, Mail, MapPin, Search, X, Calendar, Activity, AlertTriangle, User } from 'lucide-react';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';

const Pharmacies = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);

  // Modal states
  const [modal, setModal] = useState({ isOpen: false, pharmacy: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, pharmacy: null, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchPharmacies = async (currentPage = 1) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/pharmacies?page=${currentPage}&limit=10`);
      setPharmacies(res.data.data);
      setPaginationData(res.data.pagination);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pharmacies:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPharmacies(page);
  }, [page]);

  const filteredPharmacies = pharmacies.filter(p =>
    p.name?.toLowerCase().includes(filter.toLowerCase()) ||
    p.email?.toLowerCase().includes(filter.toLowerCase()) ||
    p.phone?.toLowerCase().includes(filter.toLowerCase())
  );

  const handleStatusToggle = (pharmacy) => {
    const newStatus = !pharmacy.isActive;
    const action = newStatus ? 'activate' : 'block';
    
    setConfirmModal({ 
      isOpen: true, 
      pharmacy, 
      action,
      title: `${newStatus ? 'Unblock' : 'Block'} ${pharmacy.name}?`,
      message: `Are you sure you want to ${action} this pharmacy? ${newStatus ? 'They will regain access to the system.' : 'They will be immediately locked out of their dashboard.'}`
    });
  };

  const executeStatusToggle = async () => {
    const { pharmacy, action } = confirmModal;
    const newStatus = action === 'activate';
    
    setConfirmModal({ ...confirmModal, isOpen: false });
    
    try {
      await api.post(`/api/admin/pharmacies/${pharmacy.id}/status`, { isActive: newStatus });
      setToast({ show: true, message: `Pharmacy ${newStatus ? 'unblocked' : 'blocked'} successfully!`, type: 'success' });
      fetchPharmacies(page);
      if (modal.isOpen && modal.pharmacy?.id === pharmacy.id) {
        setModal({ ...modal, pharmacy: { ...modal.pharmacy, isActive: newStatus } });
      }
    } catch (err) {
      setToast({ show: true, message: `Error: ${err.response?.data?.message || err.message}`, type: 'error' });
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ ...toast, show: false })} 
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.action === 'block' ? 'danger' : 'success'}
        confirmText={`Yes, ${confirmModal.action === 'block' ? 'Block' : 'Unblock'}`}
        onConfirm={executeStatusToggle}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Registered Pharmacies</h1>

        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search pharmacies..."
            className="pl-10 pr-4 py-2.5 sm:py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full transition-all"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Pharmacy Details Modal */}
      {modal.isOpen && modal.pharmacy && (
        <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModal({ isOpen: false, pharmacy: null })} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 sm:p-7 animate-[fadeIn_0.15s_ease-out] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                  <Building2 size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{modal.pharmacy.name}</h3>
                  <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border ${modal.pharmacy.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {modal.pharmacy.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <button onClick={() => setModal({ isOpen: false, pharmacy: null })} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Contact Information</h4>
                  <div className="flex items-center gap-3 text-gray-700 text-sm sm:text-base">
                    <Phone size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="font-medium">{modal.pharmacy.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700 text-sm sm:text-base">
                    <Mail size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="break-all">{modal.pharmacy.email || 'No email provided'}</span>
                  </div>
                  <div className="flex items-start gap-3 text-gray-700 text-sm sm:text-base">
                    <MapPin size={16} className="text-gray-400 flex-shrink-0 mt-1" />
                    <span>{modal.pharmacy.address || 'No address provided'}</span>
                  </div>
                </div>

                <div className="bg-blue-50/50 p-4 rounded-xl space-y-3 border border-blue-100">
                  <h4 className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-2">Owner Details</h4>
                  <div className="flex items-center gap-3 text-gray-700 text-sm sm:text-base">
                    <User size={16} className="text-blue-400 flex-shrink-0" />
                    <span className="font-semibold text-gray-900">{modal.pharmacy.ownerName || 'Unknown Owner'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700 text-sm sm:text-base">
                    <Phone size={16} className="text-blue-400 flex-shrink-0" />
                    <span>{modal.pharmacy.ownerPhone || 'No phone provided'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-100">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">System Details</h4>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar size={16} />
                    <span>Joined Date</span>
                  </div>
                  <span className="font-semibold text-gray-900">{new Date(modal.pharmacy.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                {modal.pharmacy.trialEndsAt && (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Activity size={16} />
                      <span>Trial Ends</span>
                    </div>
                    <span className="font-semibold text-gray-900">{new Date(modal.pharmacy.trialEndsAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {!modal.pharmacy.isActive && (
                <div className="bg-red-50 p-4 rounded-xl flex gap-3 text-sm text-red-700 border border-red-100">
                  <AlertTriangle className="flex-shrink-0" size={20} />
                  <p className="font-medium">This pharmacy account is currently inactive and cannot access the dashboard.</p>
                </div>
              )}
            </div>

            <div className="mt-8 pt-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => handleStatusToggle(modal.pharmacy)}
                className={`flex-1 py-3 rounded-xl font-bold transition-all shadow-lg ${modal.pharmacy.isActive ? 'bg-red-50 text-red-600 hover:bg-red-100 shadow-red-100' : 'bg-green-600 text-white hover:bg-green-700 shadow-green-100'}`}
              >
                {modal.pharmacy.isActive ? 'Block Pharmacy' : 'Unblock Pharmacy'}
              </button>
              <button
                onClick={() => setModal({ isOpen: false, pharmacy: null })}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Pharmacy</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Info</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Location</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Joined</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">Loading pharmacies...</td>
                </tr>
              ) : filteredPharmacies.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-primary-50 text-primary-600 rounded-lg flex-shrink-0">
                        <Building2 size={16} />
                      </div>
                      <span className="font-bold text-gray-900 text-sm sm:text-base">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="text-sm">
                      <div className="text-gray-900 font-medium">{p.phone}</div>
                      <div className="text-gray-500 truncate max-w-[160px]" title={p.email}>{p.email}</div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600 max-w-[180px]">
                      <MapPin size={14} className="flex-shrink-0 text-gray-400" />
                      <span className="truncate" title={p.address}>{p.address}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase border ${p.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-500 hidden sm:table-cell">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-right">
                    <button
                      onClick={() => setModal({ isOpen: true, pharmacy: p })}
                      className="text-primary-600 text-sm font-semibold hover:text-primary-700 transition-colors bg-primary-50 px-3 py-1.5 rounded-lg hover:bg-primary-100"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && filteredPharmacies.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    No pharmacies found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading pharmacies...</div>
          ) : filteredPharmacies.map((p) => (
            <div key={p.id} className="p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
                    <Building2 size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{p.name}</h4>
                    <p className="text-xs text-gray-500">{p.phone}</p>
                  </div>
                </div>
                <button
                  onClick={() => setModal({ isOpen: true, pharmacy: p })}
                  className="px-3 py-1.5 bg-primary-50 text-primary-600 text-xs font-bold rounded-lg hover:bg-primary-100 transition-colors"
                >
                  View
                </button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <MapPin size={14} />
                  <span className="truncate max-w-[150px]">{p.address || 'No address'}</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border ${p.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {p.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
          {!loading && filteredPharmacies.length === 0 && (
            <div className="p-6 text-center text-gray-500">No pharmacies found.</div>
          )}
        </div>

        <Pagination pagination={paginationData} onPageChange={setPage} />
      </div>
    </div>
  );
};

export default Pharmacies;
